import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Handshake, Printer, FunnelSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function SupplierHire() {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/reports/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    const params = {};
    if (start) params.start = new Date(`${start}T00:00:00`).toISOString();
    if (end) params.end = new Date(`${end}T23:59:59`).toISOString();
    if (supplierId) params.supplier_id = supplierId;
    const { data } = await api.get("/reports/supplier-hire", { params });
    setData(data);
  }, [start, end, supplierId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="pb-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-600 mb-1">Reports</div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate-900">3rd-party hire report</h1>
          <p className="text-sm text-slate-500 mt-1">Confirmed external hires by supplier — reconcile invoices against quoted prices.</p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="report-print" className="no-print"><Printer size={18} className="mr-2" />Print / export</Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4 no-print">
        <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">From (trip date)</label><input type="date" className="cd-input mt-1" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" /></div>
        <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">To (trip date)</label><input type="date" className="cd-input mt-1" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" /></div>
        <div className="sm:col-span-2"><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Supplier</label>
          <select className="cd-input mt-1" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="report-supplier">
            <option value="">All suppliers</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {/* Grand totals */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="report-totals">
          <Tot label="Hires in period" value={data.trip_count} />
          <Tot label="Total hire cost" value={fmtMoney(data.grand_total_cost)} />
          <Tot label="Total quoted" value={fmtMoney(data.grand_total_quoted)} />
          <Tot label="Total margin" value={fmtMoney(data.grand_total_margin)} highlight />
        </div>
      )}

      {/* Groups by supplier */}
      {data && data.groups.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400" data-testid="report-empty">
          <FunnelSimple size={32} className="mx-auto mb-2" />
          No confirmed 3rd-party hires in this period.
        </div>
      )}

      <div className="space-y-6">
        {(data?.groups || []).map((g) => (
          <div key={g.supplier} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`supplier-group-${g.supplier}`}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Handshake size={18} className="text-amber-500" weight="fill" />
                <h3 className="font-heading font-semibold text-slate-900">{g.supplier}</h3>
                <span className="text-xs text-slate-400">· {g.trip_count} hire{g.trip_count !== 1 ? "s" : ""}</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <span className="text-slate-500">Cost <span className="data-num font-semibold text-slate-900">{fmtMoney(g.total_cost)}</span></span>
                <span className="text-slate-500">Quoted <span className="data-num font-semibold text-slate-900">{fmtMoney(g.total_quoted)}</span></span>
                <span className="text-slate-500">Margin <span className="data-num font-semibold text-teal-700">{fmtMoney(g.total_margin)}</span></span>
              </div>
            </div>
            <div className="overflow-x-auto cd-scroll">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="bg-white text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Trip date</th>
                    <th className="px-4 py-2.5 text-left">Quote #</th>
                    <th className="px-4 py-2.5 text-left">Client</th>
                    <th className="px-4 py-2.5 text-left">Delivery location</th>
                    <th className="px-4 py-2.5 text-left">Vehicle type</th>
                    <th className="px-4 py-2.5 text-right">Supplier cost</th>
                    <th className="px-4 py-2.5 text-right">Quoted price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {g.rows.map((r) => (
                    <tr key={r.quote_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/quotes/${r.quote_id}`)} data-testid={`hire-row-${r.quote_id}`}>
                      <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-4 py-2.5 font-mono font-semibold text-slate-900">{r.quote_number}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.client}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.delivery_location}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.vehicle_type}</td>
                      <td className="px-4 py-2.5 text-right data-num text-slate-700">{fmtMoney(r.cost)}</td>
                      <td className="px-4 py-2.5 text-right data-num font-medium text-slate-900">{fmtMoney(r.quoted_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tot({ label, value, highlight }) {
  return <div className={`rounded-lg px-4 py-3 border ${highlight ? "bg-teal-50 border-teal-200" : "bg-white border-slate-200"}`}><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className={`data-num font-semibold ${highlight ? "text-teal-700" : "text-slate-900"}`}>{value}</div></div>;
}
